/**
 * Front page session manager used to connect to the socket server and listen for events, such as updates from the live editor
 * Implements self refresh
 *
 * Copyright (c) 2022 Viscomp LLC. All rights are reserved.
 *
 * @author Nickolay Kehayov <n.kehayov@viscomp.bg>
 */

const punycode = require("punycode/");

/**
 * Socket server manager
 * @type SocketManager function
 */
var SocketManager = function () {
  var counter = 1;
  var getSocketId = function () {
    return counter++;
  };

  /**
   * @param {string} url
   * @param {onMessage: {[key: msgName]: (data:Object)=>void}} settings
   */
  var SocketManager = function (url, settings) {
    this.connectionOpened = false;
    this.msgQueue = [];
    this.onMessage = settings && settings.onMessage || {};
    this.promises = [];
    this.ws = undefined;
    this.reconnecting = false;
    if (url && typeof url === "string") {
      this.connect(url);
    }
  };
  SocketManager.prototype.bindOnMessage = function (msg, cb) {
    this.onMessage[msg] = cb;
  };
  SocketManager.prototype.buildMsg = function (method, params) {
    return {
      jsonrpc: "2.0",
      method: method,
      params: params,
      id: getSocketId()
    };
  };
  SocketManager.prototype.getSocketInstance = function () {
    return this.ws;
  };

  /**
   *
   * @param {type} method
   * @param {type} params
   * @returns {Promise}
   */
  SocketManager.prototype.send = function (method, params) {
    var data = this.buildMsg(method, params);
    var promise = new Promise(function (resolve, reject) {
      this.promises[data.id] = {
        resolve: resolve,
        reject: reject
      };
    }.bind(this));
    if (!this.connectionOpened) {
      this.msgQueue.push(JSON.stringify(data));
    } else {
      this.ws.send(JSON.stringify(data));
    }
    return promise;
  };
  SocketManager.prototype.tryToReconnect = function (address) {
    var intervalMax = 5; // in sec
    var currentSec = 1;
    var intervalSec = 1000; // in milliseconds
    this.reconnecting = true;
    var intervalId = setInterval(() => {
      if (currentSec === intervalMax) {
        // retry
        this.connect(address);
        this.reconnecting = false;
        clearInterval(intervalId);
      } else {
        currentSec = currentSec + 1;
      }
    }, intervalSec);
  };
  SocketManager.prototype.connect = function (address) {
    this.ws = new WebSocket(address);
    var failureMsg = "Session connection failed. Attempting reconnect in 5 seconds.";
    this.ws.onerror = function () {
      console.log(failureMsg);
      this.tryToReconnect(address);
    }.bind(this);
    this.ws.onclose = function () {
      if (this.reconnecting === false) {
        console.log(failureMsg);
        this.tryToReconnect(address);
      }
    }.bind(this);
    this.ws.onopen = function () {
      this.connectionOpened = true;
      if (this.msgQueue.length) {
        // send all queued messages
        this.msgQueue.forEach(function (msg) {
          this.ws.send(msg);
        }.bind(this));

        // clear the queue
        this.msgQueue = [];
      }
      console.log("OPEN SOCKET CONNECTION TO:", address);
    }.bind(this);
    this.ws.onmessage = function (event) {
      var data = JSON.parse(event.data);
      var method = data.method;
      if (method) {
        this.onMessage[method] && this.onMessage[method](data);
        return;
      }
      if (data.id !== undefined && this.promises[data.id] !== undefined) {
        this.promises[data.id].resolve(data);
        delete this.promises[data.id];
      }
    }.bind(this);
  };
  return SocketManager;
}();

/**
 * Start connection to socket server and listen for events
 */
let StartFrontPageSessionManager = function () {
  /**
   * Const name that tells the vhost naming scheme used for DFS worker vhosts
   * that map the website worker hostname to the real ip:port that the worker runs
   * for example,  abv.duess.dfs  will have a vhost worker-abv.duess.dfs
   */
  const DFS_WORKER_VHOST_WEBSITE_PREFIX = "worker-";
  this.socketManager = new SocketManager();
  let sessionManagerConfig = window.smConfig;

  //only start to configure session manager if config is present
  if (!sessionManagerConfig) {
    return false;
  }
  //only connect to and activate self-refresh through session manager based on configuration
  if (sessionManagerConfig.enabled !== true) {
    return false;
  }
  let address = sessionManagerConfig.hostname + ":" + sessionManagerConfig.port + "/" + sessionManagerConfig.path;
  if (sessionManagerConfig.secure) {
    address = "wss://" + address;
  } else {
    address = "ws://" + address;
  }
  this.socketManager.connect(address);

  /**
   * @param {string} msg
   * @param {onMessage: { [key: msgName]: (data:Object) => void} } settings
   */
  this.onMessage = function (msg, cb) {
    this.socketManager.bindOnMessage(msg, cb);
  };
  this.close = function () {
    this.socketManager.getSocketInstance().close();
  };

  //listen for resource.published and upon receiving an event for this domain, refresh page
  this.onMessage("resource.published", function (data) {
    if (data.params && data.params.websiteDomain) {
      //if message through the socket is for the same domain we are browsing, refresh
      //this is needed as socket server will notify all connected clients
      //@todo: once/if we migrate to WAMP2 , use namespaces by website domain
      //note:  also handle worker- vhosts of websites that are on dfs
      let utf8CurrentDomain = punycode.toUnicode(window.location.hostname);
      let utf8RequestedDomain = punycode.toUnicode(data.params.websiteDomain);
      if (utf8CurrentDomain == utf8RequestedDomain || utf8CurrentDomain == DFS_WORKER_VHOST_WEBSITE_PREFIX + utf8RequestedDomain) {
        window.location.reload();
      }
    }
  });
};
export { StartFrontPageSessionManager };